import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Typography,
  Space,
  Progress,
  List,
  Avatar,
  Tag,
  Button,
  Select,
  App,
} from 'antd';
import {
  DashboardOutlined,
  TrophyOutlined,
  HeartOutlined,
  EyeOutlined,
  StarOutlined,
  RiseOutlined,
  GlobalOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Area,
  AreaChart,
} from 'recharts';
import { useAppSelector } from '../store/hooks';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;
const { Option } = Select;

interface DashboardStats {
  totalRatings: number;
  totalFavorites: number;
  totalBrowsed: number;
  averageRating: number;
  categoryDistribution: Array<{ name: string; value: number; color: string }>;
  ratingTrends: Array<{ date: string; ratings: number; favorites: number }>;
  topBrands: Array<{
    id: number;
    name: string;
    rating: number;
    category: string;
  }>;
  industryComparison: Array<{
    industry: string;
    average: number;
    userAverage: number;
  }>;
}

const DashboardPage: React.FC = () => {
  const { isAuthenticated } = useAppSelector(state => state.auth);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('30days');
  const { message } = App.useApp();
  const [stats, setStats] = useState<DashboardStats>({
    totalRatings: 0,
    totalFavorites: 0,
    totalBrowsed: 0,
    averageRating: 0,
    categoryDistribution: [],
    ratingTrends: [],
    topBrands: [],
    industryComparison: [],
  });

  // 模拟数据
  const mockStats: DashboardStats = {
    totalRatings: 47,
    totalFavorites: 23,
    totalBrowsed: 156,
    averageRating: 3.8,
    categoryDistribution: [
      { name: t('brands.fashion'), value: 35, color: 'var(--app-primary-color)' },
      { name: 'Cosmetics', value: 25, color: '#52c41a' },
      { name: 'Food', value: 20, color: '#faad14' },
      { name: 'Home', value: 15, color: '#eb2f96' },
      { name: 'Others', value: 5, color: '#722ed1' },
    ],
    ratingTrends: [
      { date: '2024-08-01', ratings: 5, favorites: 3 },
      { date: '2024-08-08', ratings: 8, favorites: 5 },
      { date: '2024-08-15', ratings: 12, favorites: 7 },
      { date: '2024-08-22', ratings: 15, favorites: 9 },
      { date: '2024-08-29', ratings: 18, favorites: 12 },
      { date: '2024-09-05', ratings: 22, favorites: 15 },
    ],
    topBrands: [
      { id: 1, name: 'Patagonia', rating: 4.8, category: '运动用品' },
      { id: 2, name: 'Eileen Fisher', rating: 4.6, category: '时尚' },
      { id: 3, name: "Ben & Jerry's", rating: 4.5, category: 'Food' },
      { id: 4, name: 'The Body Shop', rating: 4.3, category: 'Cosmetics' },
      { id: 5, name: 'IKEA', rating: 4.1, category: 'Home' },
    ],
    industryComparison: [
      { industry: t('brands.fashion'), average: 3.2, userAverage: 3.8 },
      { industry: 'Cosmetics', average: 3.5, userAverage: 4.1 },
      { industry: 'Food', average: 3.8, userAverage: 3.9 },
      { industry: 'Home', average: 3.4, userAverage: 3.6 },
      { industry: 'Electronics', average: 2.9, userAverage: 3.2 },
    ],
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadDashboardData();
    }
  }, [isAuthenticated, timeRange]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // 实际应该调用API获取数据
      // const response = await apiClient.get('/dashboard/stats', { params: { timeRange } });
      // setStats(response.data.data);

      // 模拟加载延迟
      setTimeout(() => {
        setStats(mockStats);
        setLoading(false);
      }, 1000);
    } catch (error) {
      message.error(t('common.error'));
      setStats(mockStats);
      setLoading(false);
    }
  };

  const renderOverviewCards = () => (
    <Row gutter={16} style={{ marginBottom: 24 }}>
      <Col span={6}>
        <Card>
          <Statistic
            title={t('dashboard.total_ratings')}
            value={stats.totalRatings}
            prefix={<StarOutlined style={{ color: '#faad14' }} />}
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card>
          <Statistic
            title={t('dashboard.total_favorites')}
            value={stats.totalFavorites}
            prefix={<HeartOutlined style={{ color: '#eb2f96' }} />}
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card>
          <Statistic
            title={t('dashboard.total_browsed')}
            value={stats.totalBrowsed}
            prefix={<EyeOutlined style={{ color: 'var(--app-primary-color)' }} />}
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card>
          <Statistic
            title={t('dashboard.average_rating')}
            value={stats.averageRating}
            precision={1}
            prefix={<TrophyOutlined style={{ color: '#52c41a' }} />}
            suffix="/5"
          />
        </Card>
      </Col>
    </Row>
  );

  const renderCategoryDistribution = () => (
    <Card
      title={
        <Space>
          <BarChartOutlined />
          <span>{t('dashboard.distribution')}</span>
        </Space>
      }
      style={{ height: 400 }}
    >
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={stats.categoryDistribution}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }: any) =>
              `${name} ${(percent * 100).toFixed(0)}%`
            }
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
          >
            {stats.categoryDistribution.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    </Card>
  );

  const renderActivityTrends = () => (
    <Card
      title={
        <Space>
          <RiseOutlined />
          <span>{t('dashboard.activity_trends')}</span>
        </Space>
      }
      style={{ height: 400 }}
    >
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={stats.ratingTrends}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Area
            type="monotone"
            dataKey="ratings"
            stackId="1"
            stroke="var(--app-primary-color)"
            fill="var(--app-primary-color)"
            name={t('dashboard.ratings_count')}
          />
          <Area
            type="monotone"
            dataKey="favorites"
            stackId="1"
            stroke="#eb2f96"
            fill="#eb2f96"
            name={t('dashboard.favorites_count')}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Card>
  );

  const renderTopBrands = () => (
    <Card
      title={
        <Space>
          <TrophyOutlined />
          <span>{t('dashboard.my_top_brands')}</span>
        </Space>
      }
      style={{ height: 400 }}
    >
      <List
        dataSource={stats.topBrands}
        renderItem={(brand, index) => (
          <List.Item>
            <List.Item.Meta
              avatar={
                <Avatar
                  style={{
                    backgroundColor: index < 3 ? '#faad14' : '#f0f0f0',
                    color: index < 3 ? '#fff' : '#999',
                  }}
                >
                  {index + 1}
                </Avatar>
              }
              title={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Text strong>{brand.name}</Text>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <Text style={{ color: '#faad14', fontWeight: 'bold' }}>
                      {brand.rating.toFixed(1)}
                    </Text>
                    <StarOutlined style={{ color: '#faad14' }} />
                  </div>
                </div>
              }
              description={<Tag color="blue">{brand.category}</Tag>}
            />
          </List.Item>
        )}
      />
    </Card>
  );

  const renderIndustryComparison = () => (
    <Card
      title={
        <Space>
          <GlobalOutlined />
          <span>{t('dashboard.industry_comparison')}</span>
        </Space>
      }
      style={{ height: 400 }}
    >
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={stats.industryComparison}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="industry" />
          <YAxis domain={[0, 5]} />
          <Tooltip />
          <Legend />
          <Bar
            dataKey="average"
            fill="#f0f0f0"
            name={t('dashboard.industry_avg')}
          />
          <Bar
            dataKey="userAverage"
            fill="var(--app-primary-color)"
            name={t('dashboard.my_avg')}
          />
        </BarChart>
      </ResponsiveContainer>
      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {/* Keep a concise note or consider removing for internationalization complexity */}
        </Text>
      </div>
    </Card>
  );

  const renderPersonalInsights = () => (
    <Card title={t('dashboard.insights_title')} style={{ marginTop: 16 }}>
      <Row gutter={16}>
        <Col span={8}>
          <div style={{ textAlign: 'center', padding: 16 }}>
            <div style={{ fontSize: 24, color: '#52c41a', marginBottom: 8 }}>
              🌱
            </div>
            <Text strong>{t('dashboard.insights_env_title')}</Text>
            <div style={{ marginTop: 8 }}>
              <Progress percent={85} size="small" />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('dashboard.insights_env_desc')}
              </Text>
            </div>
          </div>
        </Col>
        <Col span={8}>
          <div style={{ textAlign: 'center', padding: 16 }}>
            <div style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }}>
              👥
            </div>
            <Text strong>{t('dashboard.insights_social_title')}</Text>
            <div style={{ marginTop: 8 }}>
              <Progress percent={72} size="small" />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('dashboard.insights_social_desc')}
              </Text>
            </div>
          </div>
        </Col>
        <Col span={8}>
          <div style={{ textAlign: 'center', padding: 16 }}>
            <div style={{ fontSize: 24, color: '#eb2f96', marginBottom: 8 }}>
              🐾
            </div>
            <Text strong>{t('dashboard.insights_animal_title')}</Text>
            <div style={{ marginTop: 8 }}>
              <Progress percent={91} size="small" />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('dashboard.insights_animal_desc')}
              </Text>
            </div>
          </div>
        </Col>
      </Row>
    </Card>
  );

  if (!isAuthenticated) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <DashboardOutlined
              style={{ fontSize: 64, color: '#ccc', marginBottom: 16 }}
            />
            <Title level={3} type="secondary">
              {t('dashboard.please_login_title')}
            </Title>
            <Text type="secondary">{t('dashboard.please_login_desc')}</Text>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <Title level={2}>
          <Space>
            <DashboardOutlined />
            <span>{t('dashboard.title')}</span>
          </Space>
        </Title>
        <Space>
          <Select
            value={timeRange}
            onChange={setTimeRange}
            style={{ width: 120 }}
          >
            <Option value="7days">{t('dashboard.last_7_days')}</Option>
            <Option value="30days">{t('dashboard.last_30_days')}</Option>
            <Option value="90days">{t('dashboard.last_3_months')}</Option>
            <Option value="1year">{t('dashboard.last_1_year')}</Option>
          </Select>
          <Button onClick={loadDashboardData} loading={loading}>
            {t('dashboard.refresh')}
          </Button>
        </Space>
      </div>

      {renderOverviewCards()}

      <Row gutter={16}>
        <Col span={12}>{renderCategoryDistribution()}</Col>
        <Col span={12}>{renderTopBrands()}</Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>{renderActivityTrends()}</Col>
        <Col span={12}>{renderIndustryComparison()}</Col>
      </Row>

      {renderPersonalInsights()}

      <div
        style={{
          marginTop: 24,
          padding: 16,
          backgroundColor: '#f6ffed',
          borderRadius: 8,
          border: '1px solid #b7eb8f',
        }}
      >
        <Space>
          <RiseOutlined style={{ color: '#52c41a' }} />
          <div>
            <Text strong>{t('dashboard.highlight_title')}</Text>
            <Text style={{ marginLeft: 8 }}>
              {t('dashboard.highlight_text', {
                ratings: stats.totalRatings,
                favorites: stats.totalFavorites,
                avg: stats.averageRating.toFixed(1),
              })}
            </Text>
          </div>
        </Space>
      </div>
    </div>
  );
};

export default DashboardPage;
